import React, { lazy, useState } from "react";
import FAQ from "../../pages/Faq";
import { useLocation, useNavigate } from "react-router-dom";

import { Helmet } from "react-helmet-async";
import {
  FaUserTie,
  FaBriefcase,
  FaRegFileLines,
  FaUserCheck,
  FaCoins,
  FaArrowsRotate,
  FaFileSignature,
  FaPenNib,
  FaEnvelopeOpenText,
  FaEnvelope,
  FaCaretRight,
  FaBuilding,
  FaIdCard,
  FaClock,
  FaListCheck,
  FaThumbsUp,
} from "react-icons/fa6";

import {
  FiClock,
  FiFileText,
  FiAlertTriangle,
  FiMessageCircle,
  FiUsers,
  FiBriefcase,
} from "react-icons/fi";
import { Check } from "lucide-react";
import ExperienceLetterGeneratorForm from "./ExperienceLetterGeneratorForm";
const steps = [
  {
    number: 1,
    title: "Enter Company Details",
    description:
      "Add the organisation’s name, address, contact details and logo",
  },
  {
    number: 2,
    title: "Add Employee Information",
    description: "Enter the employee’s name and identification details",
  },
  {
    number: 3,
    title: "Provide Employment Detes",
    description:
      "Add the job title, department, joining date, work location and reporting manager",
  },
  {
    number: 4,
    title: "Enter Job Information",
    description:
      "Provide the employee’s designation, department, location and reporting manager",
  },
  {
    number: 5,
    title: "Add Responsibilities",
    description:
      "Enter the employee’s main duties, skills, projects and contributions",
  },
  {
    number: 6,
    title: "Select Performance Details",
    description: "Choose an appropriate performance and conduct statement",
  },
  {
    number: 7,
    title: "Generate the Letter",
    description:
      "The system will insert the information into the standard Experience Letter Template",
  },
  {
    number: 8,
    title: "Review and Issue",
    description:
      "Verify all information before signing, downloading or sharing the document",
  },
];
const features = [
  <>
    <strong>Startups & SMEs:</strong> Quickly prepare experience certificates
    when employees leave the organisation without spending time creating
    documents manually.
  </>,
  <>
    <strong>HR & Admin Teams:</strong> Create consistent experience letters for
    employees without repeatedly editing and formatting documents.
  </>,
  <>
    <strong>Recruitment Agencies:</strong> Prepare experience letters for
    contract, temporary, and placed employees quickly and professionally.
  </>,
  <>
    <strong>Consultants & Professional Firms:</strong> Generate employment
    documents for consultants, trainees, and project staff with a consistent
    professional format.
  </>,
  <>
    <strong>Schools & Educational Institutions:</strong> Issue experience
    certificates for teachers, administrators, and support employees when formal
    employment records are required.
  </>,
  <>
    <strong>Employees & Employers:</strong> Create a professional experience
    letter when a formal record of previous employment is required for future
    jobs, verification, or documentation.
  </>,
];
const Faqs1 = [
  {
    title: "Can I Create an Experience Letter Online for Free?",
    content:
      "Yes. You can create an experience letter online by entering the required employee and employment details into the generator. Once completed, the experience letter can be generated and downloaded as a professional PDF.",
  },
  {
    title: "Can I Download the Experience Letter as a PDF?",
    content:
      "Yes. After entering the required information, the generator creates a formatted experience letter that you can download as a PDF, making it ready to save, print, or share with the employee.",
  },
  {
    title: "What Information Do I Need to Create an Experience Letter?",
    content:
      "You typically need the employee’s name, job title, department, joining date, last working date, company details, and other relevant employment information required for the experience certificate.",
  },
  {
    title: "Can I Create an Experience Certificate Without Using a Template?",
    content:
      "Yes. Instead of manually editing an experience letter template, you can enter the required details into the online generator. The tool organizes the information into a professional document automatically.",
  },
  {
    title: "Can HR Teams Use an Online Experience Letter Generator?",
    content:
      "Yes. HR professionals, business owners, and administrators can use it to create experience letters for employees without repeatedly formatting documents. It is particularly useful when multiple employee documents need to be prepared.",
  },
  {
    title: "How Quickly Can I Generate an Experience Letter?",
    content:
      "You can create an experience letter in minutes by entering the required employment details. Once the information is complete, generate the document and download the finished experience certificate as a PDF.",
  },
];
const schema = [
  // =========================================================
  // SCHEMA 1: WebApplication
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Waggex Free Experience Letter Generator",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "Free online experience letter generator. Enter company details, employee information, employment dates, and work responsibilities to create and download a professional experience letter PDF instantly.",
    url: "https://www.waggex.com/hr-documents/experience-letter-generator",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    featureList: [
      "Free experience letter generator India",
      "Online experience letter format India",
      "Experience letter PDF download",
      "Work experience certificate online India",
      "Professional experience letter template India",
    ],
  },

  // =========================================================
  // SCHEMA 2: FAQPage
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is an experience letter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "An Experience Letter is an official document issued by an employer to confirm an employee's designation, employment duration, and work experience. It is used for job applications, background verification, and formal proof of previous employment.",
        },
      },
      {
        "@type": "Question",
        name: "What details should be included in an experience letter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "An experience letter should include employee name, designation, department, joining date, last working date, employment duration, roles and responsibilities, a conduct statement, company details, and the authorised HR signatory.",
        },
      },
      {
        "@type": "Question",
        name: "When should an experience letter be issued?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "An experience letter should be issued on or shortly after the employee's last working day, ideally alongside the relieving letter, so the employee can use it for their next role or background verification.",
        },
      },
      {
        "@type": "Question",
        name: "Is an experience letter the same as a relieving letter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. An experience letter confirms designation and tenure. A relieving letter formally relieves the employee from duties and confirms notice period completion. Both are typically issued together but serve different purposes.",
        },
      },
      {
        "@type": "Question",
        name: "Should salary details be included in an experience letter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Standard experience letters do not include salary details. If salary information is required, a separate salary certificate is the appropriate document. The Waggex generator follows this standard practice.",
        },
      },
    ],
  },

  // =========================================================
  // SCHEMA 3: Organization
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
    description:
      "Waggex provides free HR document generation tools and HRMS software for Indian businesses.",
    areaServed: "IN",
  },
];

const experienceLetterContent = [
  {
    icon: FaBuilding,
    title: "Company Information",
    description:
      "The company name, address, logo, contact details and issue date.",
  },
  {
    icon: FaIdCard,
    title: "Employee Identification",
    description: "The employee’s full name and employee identification number.",
  },
  {
    icon: FaClock,
    title: "Employment Duration",
    description: "The official joining date and last working date.",
  },
  {
    icon: FaBriefcase,
    title: "Designation and Department",
    description: "The employee’s final job title and department.",
  },
  {
    icon: FaListCheck,
    title: "Roles and Responsibilities",
    description:
      "A summary of the employee’s primary duties and contributions.",
  },
  {
    icon: FaUserCheck,
    title: "Performance and Conduct",
    description:
      "An optional statement describing the employee’s work and behaviour.",
  },
  {
    icon: FaThumbsUp,
    title: "Appreciation Statement",
    description:
      "A professional closing message recognising the employee’s contribution and wishing them success.",
  },
  {
    icon: FaPenNib,
    title: "Authorised Signature",
    description:
      "The name and designation of the authorised representative issuing the letter.",
  },
];
const experienceLetterPoints = [
  "Employee Identification",
  "Designation and Department",
  "Employment Duration",
  "Employment Type",
  "Roles and Performance",
  "Official Authorisation",
];

const companystepsFeatures = [
  {
    id: 1,
    icon: <FiClock />,
    title: "Save HR Time",
    desc: "Create professional experience letters quickly without drafting each letter from scratch or repeatedly copying candidate details",
  },
  {
    id: 2,
    icon: <FiFileText />,
    title: "Maintain a Standard Format",
    desc: "Use a consistent experience letter format for different candidates while keeping important job and employment details clearly organised",
  },
  {
    id: 3,
    icon: <FiAlertTriangle />,
    title: "Reduce Manual Errors",
    desc: "Enter candidate and employment details once and place them in the relevant sections of the experience letter without repeated manual editing",
  },
  {
    id: 4,
    icon: <FiMessageCircle />,
    title: "Improve Candidate Communication",
    desc: "Clearly share important details such as the job role, salary, joining date, and other employment terms with selected candidates",
  },
  {
    id: 5,
    icon: <FiUsers />,
    title: "Support Faster Hiring",
    desc: "Generate and share experience letters soon after selecting a candidate, helping you complete the hiring process without unnecessary delays",
  },
  {
    id: 6,
    icon: <FiBriefcase />,
    title: "Suitable for Different Businesses",
    desc: "Create experience letters for your hiring needs, whether you’re a startup, small business, recruiter, or growing organisation",
  },
];

const allexperiencePoints = [
  "Employee name",
  "Job title",
  "Joining date",
  "Last working date",
  "Employment duration",
];

const experiencePoints = [
  "Employment duration",
  "Designation",
  "Department",
  "Responsibilities",
  "Performance and conduct",
  "Appreciation message",
];
export default function ExperienceLetterGenerator() {
  return (
    <>
      <Helmet>
        <title>Free Experience Letter Generator Online | PDF Format</title>
        <meta
          name="description"
          content="Create a professional experience letter online for free. Add employee details, job dates and work info, then download your PDF instantly"
        />
        <meta
          name="keywords"
          content="experience letter generator online,experience letter format India,free experience letter maker online India,experience letter template India,experience letter PDF generator India,job experience certificate online India,employee experience letter format India,experience letter Word format India,work experience letter India"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/hr-documents/experience-letter-generator"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <div className="px-2 py-3 sm:px-4">
        <section className="rounded-xl border border-white px-2 sm:px-5 py-7 shadow-sm backdrop-blur bg-[#ffffff40]">
          <div className="text-center  mb-10">
            <h1 className="text-[#10324F] text-lg sm:text-3xl font-roboto font-bold leading-snug mb-4">
              Free Experience Letter Generator Online
            </h1>

            <p className="mx-auto mt-8 max-w-5xl text-sm sm:text-lg leading-6 text-[#10324F] min-h-18 sm:min-h-12">
              Create a professional experience letter for employees using a
              ready-to-use HR template. Enter the basic details, generate the
              letter, and download it as a PDF
            </p>

            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row mb-10">
              <button
                onClick={() =>
                  (window.location.href = "https://app.waggex.com/signup")
                }
                className="w-full sm:w-auto bg-[#10324F] hover:bg-[#10324F] transition-all duration-300 text-white px-14 py-3 rounded-xl shadow-md text-sm sm:text-base font-medium"
              >
                Start Free Trial
              </button>

              <button
                onClick={() => (window.location.href = "/contact-us")}
                className="w-full sm:w-auto border border-[#10324F] text-[#10324F] hover:bg-[#10324F] hover:text-white transition-all duration-300 px-14 py-3 rounded-xl text-sm sm:text-base font-medium"
              >
                Book a Demo
              </button>
            </div>
          </div>
          <ExperienceLetterGeneratorForm />
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 ">
                {/* Left Content */}
                <div>
                  <div className="mt-4">
                    <div className="mb-6 flex items-center gap-3">
                      <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                      <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                        WHAT IS AN EXPERIENCE LETTER?
                      </span>
                    </div>

                    <h2 className="text-xl md:text-3xl font-semibold text-[#10324F] mt-3 mb-5">
                      Official Proof of Employment and Work Experience
                    </h2>

                    <p className="text-[#10324F] mb-6 leading-relaxed max-w-2xl">
                      An experience letter confirms an employee’s role and
                      employment period while providing formal proof of previous
                      work. Use this tool to quickly create a professional
                      experience letter, enter the required details, and
                      download the completed document as a PDF
                    </p>
                  </div>
                </div>

                {/* Right Content */}
                <div className="flex flex-col">
                  {experienceLetterPoints.map((item, index) => (
                    <React.Fragment key={item}>
                      <div className="relative z-10 flex min-h-12.5 items-center justify-center rounded-lg border border-white  bg-linear-to-b from-white to-[#D1DFEA]/80  hover:bg-white/90 px-4 py-3 text-center text-sm font-medium text-[#10324F] shadow-sm backdrop-blur-sm transition sm:min-h-12.5 sm:text-base">
                        {item}
                      </div>

                      {index < experienceLetterPoints.length - 1 && (
                        <div className="mx-auto h-6 border-l border-dashed border-[#10324F]/80" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  WHAT DOES THE EXPERIENCE LETTER TEMPLATE INCLUDE?
                </p>
                <h2 className="mt-4 text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F] uppercase">
                  Generate Experience Certificates Online
                </h2>

                <p className="mt-6 text-sm leading-6 sm:text-base text-[#10324F] px-2 font-roboto  text-center max-w-3xl mx-auto">
                  Enter employee and employment details once, and the tool
                  organizes them into a professional experience letter. Review
                  the document and download the finished PDF instantly
                </p>
              </div>

              {/* Cards */}
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                {experienceLetterContent.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={index}
                      className="group flex min-h-25.5 items-start gap-4 rounded-xl border border-white/90 bg-linear-to-b from-white/60 to-[#D1DFEA]/70  hover:bg-white/90 px-5 py-5 transition-all duration-200 shadow-sm"
                    >
                      {/* Icon */}
                      <div className="flex shrink-0 items-center justify-center pt-1">
                        <Icon className="text-2xl text-[#10324F]" />
                      </div>

                      {/* Content */}
                      <div>
                        <h3 className="text-sm sm:text-lg font-semibold leading-5 text-[#10324F]">
                          {item.title}
                        </h3>

                        <p className="mt-2 text-xs sm:text-sm leading-5 text-[#10324F]">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  HOW TO CREATE AN EXPERIENCE LETTER ONLINE
                </p>

                <h2 className="mt-4 text-xl font-bold leading-tight text-[#10324F] sm:text-3xl">
                  Generate the Document in Simple Steps
                </h2>
              </div>

              {/* Steps */}

              {/* ROW 1 */}
              <div className=" mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(0, 4).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE */}
                    <div className="absolute left-0 right-1/2 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 3 && (
                      <div className="absolute left-1/2 right-0 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#10324F] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#1324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* ROW 2 */}
              <div className=" mt-4 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(4, 8).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE — IMPORTANT FOR STEP 5 */}
                    <div className="absolute left-0 right-1/2 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 3 && (
                      <div className="absolute left-1/2 right-0 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#163957] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#10324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-8 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-8">
                {/* LEFT CONTENT */}
                <div>
                  {/* Small heading */}

                  <div className="mb-6 flex items-center gap-3">
                    <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                    <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                      UNDERSTANDING THE COMMON TERMINOLOGY
                    </span>
                  </div>

                  {/* Main heading */}
                  <h2 className="text-xl font-semibold leading-tight text-[#10324F] sm:text-3xl">
                    Experience Letter vs Experience Certificate
                  </h2>

                  {/* Intro */}
                  <p className="mt-7 max-w-2xl text-sm leading-8 text-[#10324F]">
                    An Experience Letter and experience certificate are often
                    used for the same purpose
                  </p>
                </div>

                {/* RIGHT COMPARISON */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* ================= OFFER LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-4 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FaEnvelopeOpenText className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Experience Letter
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-73 rounded-xl border border-white bg-linear-to-b  from-white/60 to-[#D1DFEA]/30 px-4 py-6 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {experiencePoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ================= APPOINTMENT LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-4 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FaEnvelope className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Experience Certificate
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-73 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-4 py-6 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {allexperiencePoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  WHY USE AN ONLINE EXPERIENCE LETTER FORMAT?
                </p>
                <h2 className="text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F]  uppercase">
                  Create Professional Employee Documents Faster
                </h2>
                {/* <p className="mt-6 text-sm leading-6 sm:text-base text-[#10324F] px-2 font-roboto  text-center max-w-3xl mx-auto">
                  Send HR announcements, payroll updates, policy changes,
                  holiday notices, attendance reminders, event updates, and
                  important company communications from one platform
                </p> */}
              </div>
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-4">
                {companystepsFeatures.map((item) => {
                  // const Icon = item.icon;

                  return (
                    <div
                      key={item.id}
                      className="rounded-xl border border-white/90  bg-linear-to-b from-white/60 to-[#D1DFEA]/60  hover:bg-white/30 p-4 duration-200 shadow-sm transition-all"
                    >
                      {/* Top Row */}
                      <div className="flex items-center justify-center mb-5">
                        {/* <div className="w-14 h-10 rounded-md bg-[#16324F] font-roboto text-white flex items-center justify-center text-lg font-bold shadow">
                          {item.id}
                        </div> */}
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#10324F] text-xl text-white">
                          {item.icon}
                        </div>
                      </div>

                      {/* Content */}
                      <h2 className="text-center text-sm sm:text-base font-semibold text-[#10324F] font-roboto">
                        {item.title}
                      </h2>

                      <p className="mt-2 text-center text-sm leading-6 text-[#10324F] font-roboto">
                        {item.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto mb-4">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  WHO CAN USE THIS EXPERIENCE LETTER FORMAT?
                </p>
                <h2 className="font-roboto text-center text-[#10324F] text-xl md:text-3xl font-bold uppercase tracking-wide">
                  Suitable for Different Employers and Employment
                </h2>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 place-items-start">
                {features.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 text-[#10324F] px-2 py-3  rounded-2xl"
                  >
                    {/* Check Icon */}
                    <div className="mt-1 flex items-center justify-center text-[#10324F]">
                      <Check size={30} aria-hidden="true" />
                    </div>

                    {/* Text */}
                    <p className="font-roboto text-sm sm:text-lg font-medium leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <FAQ
            title="Frequently Asked Question"
            items={Faqs1}
            defaultOpenIndex={0}
          />
          <section className="w-full bg-[#F4F6F8]/60 px-6 py-10 rounded-xl border border-white mt-8">
            <div className="max-w-6xl mx-auto text-center mt-4 mb-4">
              <h2 className="text-[#10324F] text-xl sm:text-3xl font-roboto font-bold uppercase">
                Create Experience Letter Document In Minutes
              </h2>

              <p className="mt-4 text-[#10324F] text-sm sm:text-base">
                Create your experience letter in minutes with no login, no
                watermark, and no waiting. Just enter the employee details,
                generate the letter, and download your PDF instantly
              </p>

              <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3
              rounded-xl
              bg-[#10324F]
              text-white text-xs sm:text-sm
              font-medium
              shadow-md
              transition-all duration-300
              hover:scale-105
            "
                  onClick={() => {
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                >
                  Generate Experience Letter
                </button>

                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3 text-xs sm:text-sm
              rounded-xl
              border border-[#10324F]
              text-[#10324F]
              font-medium
              transition-all duration-300
              hover:bg-white
              hover:text-[#16385C]
            "
                  onClick={() => (window.location.href = "/all-hr-documents")}
                >
                  Explore more
                </button>
              </div>
            </div>
          </section>
        </section>
      </div>
    </>
  );
}
